import React, { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Fab,
  LinearProgress,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Tooltip
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import axios from "axios";
import api from "../common/api";

const RebateProviders = ({ client_id, provider_id }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [orderColumn, setOrderColumn] = useState("");
  const [orderOrder, setOrderOrder] = useState("");
  const [pending, setPending] = useState(true);

  const getMuiTheme = () =>
    createMuiTheme({
      overrides: {
        MuiTableCell: {
          root: {
            padding: "0.5rem",
            fontSize: "0.8rem",
            maxWidth: "10rem"
          }
        }
      }
    });

  const fetchData = async () => {
    setIsLoading(true);
    let url = api.providers().rebateProviders();
    
    // Add parameters to URL
    const params = new URLSearchParams();
    if (client_id) params.append('client_id', client_id);
    if (provider_id) params.append('provider_id', provider_id);
    params.append('pending', pending ? '1' : '0');
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    const token = localStorage.getItem("userToken");
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Process data to add subtotal rows
      const processedData = addSubtotalRows(res.data.data || res.data);
      setData(processedData);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to add subtotal rows when provider changes
  const addSubtotalRows = (rawData) => {
    if (!rawData || rawData.length === 0) return rawData;
    
    const processedData = [];
    let currentProvider = null;
    let subtotalData = {
      rebate: 0,
      total_with_rebate: 0,
      total_s_iva_price: 0
    };
    
    rawData.forEach((row, index) => {
      const businessName = row.provider_business_name || row[0]; // Using correct property name
      
      // If provider changed, add subtotal row
      if (currentProvider && currentProvider !== businessName) {
        processedData.push({
          ...subtotalData,
          provider_business_name: `SUBTOTAL`,
          sap_code: '',
          work_order: '',
          number: '',
          'work_order.reception_sap_code': '',
          isSubtotal: true
        });
        // Reset subtotal data
        subtotalData = {
          rebate: 0,
          total_with_rebate: 0,
          total_s_iva_price: 0
        };
      }
      
      // Add current row
      processedData.push(row);
      
      // Accumulate subtotal values
      subtotalData.rebate += parseFloat(row.rebate || 0);
      subtotalData.total_with_rebate += parseFloat(row.total_with_rebate || 0);
      subtotalData.total_s_iva_price += parseFloat(row.total_s_iva_price || 0);
      
      currentProvider = businessName;
    });
    
    // Add final subtotal row
    if (currentProvider) {
      processedData.push({
        ...subtotalData,
        provider_business_name: `SUBTOTAL`,
        sap_code: '',
        work_order: '',
        number: '',
        'work_order.reception_sap_code': '',
        isSubtotal: true
      });
    }
    
    return processedData;
  };

  useEffect(() => {
    fetchData();
  }, [client_id, provider_id, pending]);

  const columns = [
    {
      name: "provider_business_name",
      label: "Nombre del Proveedor",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "provider_business_name" ? orderOrder : null,
        customBodyRender: (value, tableMeta) => {
          const isSubtotal = tableMeta.rowData[9]; // Assuming isSubtotal is the last column
          const client_id = tableMeta.rowData[10];
          return (
            <span style={{ 
              fontWeight: isSubtotal ? '600' : 'normal',
            }}>
                {isSubtotal ? value : <a href={"/clients/" + client_id} target="_blank">{value}</a>}
            </span>
          );
        }
      }
    },
    {
      name: "sap_code",
      label: "Código SAP",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "sap_code" ? orderOrder : null
      }
    },
    {
      name: "work_order.id",
      label: "Orden de Trabajo",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "work_order" ? orderOrder : null,
        customBodyRender: (value, tableMeta) => {
            return (
              <a href={"/work-order/" + value} target="_blank">{value}</a>
            );
          }
        }
    },
    {
      name: "number",
      label: "Número",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "number" ? orderOrder : null
      }
    },
    {
      name: "work_order.reception_sap_code",
      label: "Código OC Recepción",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "work_order.reception_sap_code" ? orderOrder : null
      }
    },
    {
      name: "sap_code",
      label: "Código SAP Voucher",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "sap_code" ? orderOrder : null
      }
    },
    {
      name: "rebate",
      label: "Rebate",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "rebate" ? orderOrder : null,
        customBodyRender: (value, tableMeta) => {
          const isSubtotal = tableMeta.rowData[9];
          return (
            <span style={{ 
              fontWeight: isSubtotal ? '600' : 'normal',
            }}>
              {value ? `$${parseFloat(value).toFixed(2)}` : '$0.00'}
            </span>
          );
        }
      }
    },
    {
      name: "total_with_rebate",
      label: "Total con Rebate",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "total_with_rebate" ? orderOrder : null,
        customBodyRender: (value, tableMeta) => {
          const isSubtotal = tableMeta.rowData[9];
          return (
            <span style={{ 
              fontWeight: isSubtotal ? '600' : 'normal',
            }}>
              {value ? `$${parseFloat(value).toFixed(2)}` : '$0.00'}
            </span>
          );
        }
      }
    },
    {
      name: "total_s_iva_price",
      label: "Total sin IVA",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "total_s_iva_price" ? orderOrder : null,
        customBodyRender: (value, tableMeta) => {
          const isSubtotal = tableMeta.rowData[9];
          return (
            <span style={{ 
              fontWeight: isSubtotal ? '600' : 'normal',
            }}>
              {value ? `$${parseFloat(value).toFixed(2)}` : '$0.00'}
            </span>
          );
        }
      }
    },
    {
      name: "isSubtotal",
      label: "",
      options: {
        display: false,
        viewColumns: false
      }
    },{
        name: "client_id",
        label: "",
        options: {
            display: false,
            viewColumns: false
        }
    }
  ];

  let options = {
    customToolbar: () => {
        return (
            <React.Fragment>
                <Tooltip title="Mostrar solo rebates pendientes">
                    <FormControlLabel
                        control={
                        <Switch
                            size="small"
                            onChange={(e) => setPending(e.target.checked)}
                            checked={pending}
                        />
                        }
                        label={<Typography style={{ fontSize: "0.73rem" }}>Pendientes</Typography>}
                        labelPlacement="bottom"
                        className="m-0 mt-3 mx-1"
                    />
                </Tooltip>
            </React.Fragment>
        )
    },    

    selectableRowsOnClick: false,
    selectableRows: false,
    serverSide: false,
    pagination: true,
    rowsPerPageOptions: [10, 25, 50, 100],
    onColumnSortChange: (column, direction) => {
      setOrderColumn(column);
      setOrderOrder(direction);
    },
    textLabels: {
      body: {
        noMatch: isLoading ? (
          <CircularProgress size={24} />
        ) : (
          "No hay datos disponibles"
        )
      }
    }
  };

  if (isLoading) {
    return (
      <div>
        <Typography variant="h6">Cargando...</Typography>
        <LinearProgress />
      </div>
    );
  }
  
  return (
    <>

      <MuiThemeProvider theme={getMuiTheme()}>
        <MUIDataTable
          title="Reporte de Rebates por Proveedor"
          data={data}
          columns={columns}
          options={options}
        />
      </MuiThemeProvider>
    </>
  );
};

export default RebateProviders;
